'use client';

import { useState, useEffect, type ChangeEvent } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  isValidDomain,
  isValidDomainHost,
} from '@terra/shared-contracts-applications';
import { Button } from '../../components/button';
import { LegacyDialog } from '../../components/dialog';
import { Field } from '../../components/field';
import { IconButton } from '../../components/icon-button';
import { Input } from '../../components/input';
import { Text } from '../../components/text';

type AddDomainDialogProps = {
  open: boolean;
  error?: string | null;
  hostOnly?: boolean;
  onOpenChange: (open: boolean) => void;
  onAddDomain: (domain: string) => void;
  onDomainInputChange?: (value: string) => void;
};

export const AddDomainDialog = ({
  open,
  error: externalError,
  hostOnly = true,
  onDomainInputChange,
  onOpenChange,
  onAddDomain,
}: AddDomainDialogProps) => {
  const t = useTranslations('designSystem.domainScope');
  const [domain, setDomain] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDomain('');
      setValidationError(null);
    }
  }, [open]);
  const handleAddDomain = () => {
    const isDomainValid = hostOnly
      ? isValidDomainHost(domain)
      : isValidDomain(domain);

    if (!isDomainValid) {
      setValidationError(t('invalidDomainError'));

      return;
    }

    setValidationError(null);
    onAddDomain(domain.trim());
  };

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    onDomainInputChange?.(value);
    setValidationError(null);
    setDomain(value);
  };

  const displayError = externalError ?? validationError;

  return (
    <LegacyDialog.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content width="512px" maxWidth="90%">
          <VStack gap={2} alignItems="stretch" px={6} pt={6} pb={3}>
            <HStack
              justifyContent="space-between"
              alignItems="center"
              gap="2.5"
            >
              <LegacyDialog.Title fontWeight="medium" flexGrow={1}>
                {t('addDomainDialog.title')}
              </LegacyDialog.Title>
              <LegacyDialog.CloseTrigger asChild>
                <IconButton aria-label="Close Dialog" variant="ghost" size="xs">
                  <Cancel01Icon />
                </IconButton>
              </LegacyDialog.CloseTrigger>
            </HStack>
            <LegacyDialog.Description>
              {t('addDomainDialog.description')}
            </LegacyDialog.Description>
          </VStack>
          <Box px={6} py={3}>
            <Field.Root>
              <Field.Label fontSize="xs">
                <Text as="span" color="fg.neutral.error">
                  *
                </Text>{' '}
                {t('addDomainDialog.label')}
              </Field.Label>
              <Input
                placeholder={t('addDomainDialog.placeholder')}
                value={domain}
                onChange={onSearchChange}
                size="xs"
              />
              {displayError ? (
                <Field.HelperText fontSize="xs" color="fg.neutral.error">
                  {displayError}
                </Field.HelperText>
              ) : (
                <Field.HelperText fontSize="xs" color="fg.neutral.subtle">
                  {hostOnly
                    ? t('addDomainDialog.helperText')
                    : t('hostOnlyHelper')}
                </Field.HelperText>
              )}
            </Field.Root>
          </Box>
          <HStack gap={2} justifyContent="flex-end" pt={3} pb={6} px={6}>
            <LegacyDialog.CloseTrigger asChild>
              <Button variant="outline" size="sm">
                {t('addDomainDialog.cancel')}
              </Button>
            </LegacyDialog.CloseTrigger>
            <Button
              size="sm"
              disabled={!domain.trim()}
              onClick={handleAddDomain}
            >
              {t('addDomainDialog.add')}
            </Button>
          </HStack>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
};
